import { IconActivity } from '@developerz/ui';
import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { AdminAuditRow } from '../lib/admin';
import { orgHref } from '../lib/org';
import { absolute, ago } from '../lib/time';
import { StatusPill, type StatusTone } from './StatusPill';
import { buttonClass } from './ui/Button';

// Global audit tail (admin radar). Presenter only — `.row-list` rows framed by
// the parent Panel (spec §3.15, mock SimplePage '/admin'): icon tile + event
// line left, ghost "View" into the full `/org/:org/audit/:session_id` timeline
// right. The outcome renders as a StatusPill whose tone tracks the result.
//
// THE LINK NAMES THE ROW'S OWN ORG, passed to `orgHref` explicitly: `/admin`
// sits outside `OrgScope`, so `orgHref`'s default (`urlOrg()`) is null there and
// answers a bare `/audit/<id>`, which `LegacyRedirect` resolves against the
// OPERATOR's active org, a 404 for another tenant's session. And only where the operator holds a membership on
// that org (`isMember`, the container's `me.memberships` read, the rule
// `AdminRunPanel` takes): the session page is an org-scoped read, so a link
// into a foreign org would land on `OrgScope`'s not-a-member wall. A system row
// (`account_login: null`) has no org page to open at all.

/** merged/ok → success | escalated/failed → warning | else neutral. */
function outcomeTone(outcome: string | null): StatusTone {
  if (outcome === 'merged' || outcome === 'completed' || outcome === 'ok') return 'success';
  if (outcome === 'escalated' || outcome === 'failed' || outcome === 'denied') return 'warning';
  return 'neutral';
}

export function AdminAuditTail(props: {
  readonly rows: readonly AdminAuditRow[];
  /** True when the operator holds a membership on the org named `login`. */
  readonly isMember: (login: string) => boolean;
}): JSX.Element {
  return (
    <ul class="row-list">
      <For each={props.rows}>
        {(row) => (
          <li class="row-list__row admin-audit__row">
            <div class="row-list__lead admin-audit__lead">
              <span class="row-list__tile" aria-hidden="true">
                <IconActivity />
              </span>
              <div class="admin-audit__lines">
                <p class="admin-audit__title">
                  <span>{row.tool ?? row.kind}</span>
                  <Show when={row.outcome}>
                    {(outcome) => (
                      <StatusPill tone={outcomeTone(outcome())}>{outcome()}</StatusPill>
                    )}
                  </Show>
                </p>
                <p class="admin-audit__meta">
                  {row.account_login ?? t('admin.audit_no_account')}
                  {' · '}
                  <time datetime={row.ts_iso} title={absolute(row.ts_iso)}>
                    {ago(row.ts_iso)}
                  </time>
                </p>
              </div>
            </div>
            <Show when={row.session_id}>
              {(sid) => (
                <Show when={row.account_login}>
                  {(login) => (
                    <Show
                      when={props.isMember(login())}
                      fallback={
                        <span class="admin-audit__meta">{t('admin.audit_no_membership')}</span>
                      }
                    >
                      <A
                        class={buttonClass({ variant: 'ghost', size: 'sm' })}
                        href={orgHref(`/audit/${encodeURIComponent(sid())}`, login())}
                      >
                        {t('admin.audit_view')}
                      </A>
                    </Show>
                  )}
                </Show>
              )}
            </Show>
          </li>
        )}
      </For>
    </ul>
  );
}
